// components/features/support/tickets/TicketDetailsModal.tsx
import { useState, useEffect } from "react";

import { User, Phone, Calendar, FileText, Send, Eye, EyeOff, Edit2, Save, X } from "lucide-react";

import { alertError, alertSuccess } from "../../../helpers/alertError";
import { Ticket } from "../../../types/features/Ticket";
import useAuth from "../../../hooks/features/auth/useAuth";
import { TicketMessage } from "../../../types/features/TicketMessage";
import { ticketMessageService } from "../../../services/ticketMessageService";
import { ticketService } from "../../../services/ticketService";
import BaseModal from "../../ui/Modal";

// Liste des catégories disponibles
const problemCategoriesList = [
  { name: "Inscription", slug: "inscription" },
  { name: "Connexion", slug: "connexion" },
  { name: "OTP", slug: "otp" },
  { name: "KYC", slug: "kyc" },
  { name: "Wallet", slug: "wallet" },
  { name: "Transactions", slug: "transactions" },
  { name: "Retrait", slug: "retrait" },
  { name: "Dépôt", slug: "depot" },
  { name: "Buy & Sell", slug: "buy-sell" },
  { name: "Transfert bancaire", slug: "transfert-bancaire" },
  { name: "Sécurité", slug: "securite" },
  { name: "Technique", slug: "technique" },
  { name: "Notifications", slug: "notifications" },
  { name: "Parrainage", slug: "parrainage" },
  { name: "Autre", slug: "autre" },
];

interface TicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  onStatusChange?: (id: string, status: Ticket["status"]) => void;
  onTicketUpdate?: () => void;
}

export default function TicketDetailsModal({
  isOpen,
  onClose,
  ticket,
  onStatusChange,
  onTicketUpdate,
}: TicketDetailsModalProps) {
  const { user: currentUser } = useAuth();
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(true);

  // États pour l'édition
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSolution, setEditSolution] = useState("");
  const [editCategories, setEditCategories] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && ticket) {
      loadMessages();
      // Initialiser les valeurs d'édition
      setEditTitle(ticket.title || "");
      setEditDescription(ticket.description || "");
      setEditSolution(ticket.solution || "");
      setEditCategories(ticket.problem_categories || []);
      setIsEditing(false);
    }
  }, [isOpen, ticket]);

  const loadMessages = async () => {
    if (!ticket) return;
    setLoading(true);
    try {
      const data = await ticketMessageService.getMessages(ticket.id);
      setMessages(data);
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !ticket || !currentUser) return;

    setSending(true);
    try {
      const messageData = {
        ticket_id: ticket.id,
        author_type: "agent" as const,
        author_id: currentUser.id,
        message: newMessage.trim(),
        is_internal: isInternalNote,
      };

      const sentMessage = await ticketMessageService.sendMessage(messageData);
      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage("");
      setIsInternalNote(false);

      alertSuccess(isInternalNote ? "Note interne ajoutée" : "Message envoyé");

      if (onTicketUpdate) onTicketUpdate();
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      alertError("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!ticket) return;

    setSaving(true);
    try {
      await ticketService.updateTicket(ticket.id, {
        title: editTitle,
        description: editDescription,
        solution: editSolution,
        problem_categories: editCategories,
      });

      alertSuccess("Modifications enregistrées");
      setIsEditing(false);

      // Mettre à jour le ticket localement
      ticket.title = editTitle;
      ticket.description = editDescription;
      ticket.solution = editSolution;
      ticket.problem_categories = editCategories;

      if (onTicketUpdate) onTicketUpdate();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alertError("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (slug: string) => {
    setEditCategories((prev) => (prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug]));
  };

  const getPriorityLabel = () => {
    switch (ticket?.priority) {
      case "critique":
        return { label: "Critique", className: "bg-red-100 text-red-700" };
      case "haute":
        return { label: "Haute", className: "bg-orange-100 text-orange-700" };
      case "moyenne":
        return { label: "Moyenne", className: "bg-yellow-100 text-yellow-700" };
      case "basse":
        return { label: "Basse", className: "bg-green-100 text-green-700" };
      default:
        return { label: "Moyenne", className: "bg-gray-100 text-gray-700" };
    }
  };

  const getStatusLabel = () => {
    switch (ticket?.status) {
      case "nouveau":
        return { label: "Nouveau", className: "bg-blue-100 text-blue-700" };
      case "en_cours":
        return { label: "En cours", className: "bg-purple-100 text-purple-700" };
      case "en_attente":
        return { label: "En attente", className: "bg-yellow-100 text-yellow-700" };
      case "escalade":
        return { label: "Escalade", className: "bg-red-100 text-red-700" };
      case "resolu":
        return { label: "Résolu", className: "bg-green-100 text-green-700" };
      case "ferme":
        return { label: "Fermé", className: "bg-gray-100 text-gray-700" };
      default:
        return { label: "Nouveau", className: "bg-gray-100 text-gray-700" };
    }
  };

  const priority = getPriorityLabel();
  const status = getStatusLabel();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSourceLabel = () => {
    switch (ticket?.source) {
      case "appel":
        return "Appel téléphonique";
      case "formulaire_web":
        return "Formulaire web";
      case "email":
        return "Email";
      default:
        return ticket?.source || "Inconnu";
    }
  };

  const getCategoryName = (slug: string) => {
    const cat = problemCategoriesList.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  const visibleMessages = messages.filter((msg) => showInternalNotes || !msg.is_internal);

  if (!ticket) return null;

  const isClosed = ticket.status === "ferme" || ticket.status === "resolu";
  const canSendMessage = !isClosed && currentUser;

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={`Ticket - ${ticket.title.substring(0, 50)}`} size="full">
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Contenu principal avec scroll */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* En-tête avec statut et priorité */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${priority.className}`}>
                {priority.label}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-400">{getSourceLabel()}</div>
              {!isEditing && !isClosed && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Modifier le ticket"
                >
                  <Edit2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Numéro de ticket */}
          <div className="text-right">
            <span className="text-xs text-gray-400">Ticket #{ticket.ticket_number || ticket.id}</span>
          </div>

          {/* Titre - MODIFIABLE */}
          <div>
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            ) : (
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{ticket.title}</h3>
            )}
          </div>

          {/* Description - MODIFIABLE */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={16} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Préoccupation du client </span>
            </div>
            {isEditing ? (
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
              />
            ) : (
              <p className="text-gray-600 text-sm whitespace-pre-wrap">{ticket.description}</p>
            )}
          </div>

          {/* Catégories du problème - MODIFIABLE */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Catégories du problème</h4>
            </div>
            {isEditing ? (
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {problemCategoriesList.map((cat) => (
                    <button
                      key={cat.slug}
                      type="button"
                      onClick={() => toggleCategory(cat.slug)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        editCategories.includes(cat.slug)
                          ? "bg-green-600 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
                {editCategories.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Catégories sélectionnées :</p>
                    <div className="flex flex-wrap gap-1">
                      {editCategories.map((slug) => (
                        <span key={slug} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                          {getCategoryName(slug)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {ticket.problem_categories && ticket.problem_categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {ticket.problem_categories.map((cat, idx) => (
                      <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                        {getCategoryName(cat)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Aucune catégorie</p>
                )}
              </div>
            )}
          </div>

          {/* Solution - MODIFIABLE */}
          <div
            className={`rounded-lg p-4 border ${isEditing ? "bg-white border-blue-300" : "bg-green-50 border-green-200"}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className={`text-sm font-medium ${isEditing ? "text-gray-700" : "text-green-800"}`}>
                Réponse apportée
              </h4>
            </div>
            {isEditing ? (
              <textarea
                value={editSolution}
                onChange={(e) => setEditSolution(e.target.value)}
                rows={3}
                placeholder="Réponse apportée au problème..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
              />
            ) : (
              <p className={`text-sm ${isEditing ? "text-gray-700" : "text-green-700"} whitespace-pre-wrap`}>
                {ticket.solution || <span className="italic text-gray-400">Aucune réponse renseignée</span>}
              </p>
            )}
          </div>

          {/* Boutons d'action en mode édition */}
          {isEditing && (
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditTitle(ticket.title || "");
                  setEditDescription(ticket.description || "");
                  setEditSolution(ticket.solution || "");
                  setEditCategories(ticket.problem_categories || []);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <X size={16} className="inline mr-1" />
                Annuler
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save size={16} className="inline mr-1" />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          )}

          {/* Informations client */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Client</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <User size={14} className="text-gray-400" />
                <span className="text-gray-600">{ticket.client_name || "Client inconnu"}</span>
              </div>
              {ticket.client_phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-gray-400" />
                  <span className="text-gray-600">{ticket.client_phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Messages / Conversation */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">Conversation</h4>
              <button
                onClick={() => setShowInternalNotes(!showInternalNotes)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                {showInternalNotes ? <EyeOff size={14} /> : <Eye size={14} />}
                {showInternalNotes ? "Masquer" : "Afficher"} notes internes
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8 text-gray-500">Chargement des messages...</div>
            ) : visibleMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Aucun message</div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {visibleMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg ${
                      msg.is_internal
                        ? "bg-yellow-50 border border-yellow-200"
                        : msg.author_type === "client"
                          ? "bg-blue-50 border border-blue-200"
                          : "bg-white border border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">
                        {msg.author_type === "client"
                          ? "Client"
                          : msg.is_internal
                            ? "📝 Note interne - Agent"
                            : "Agent"}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Zone d'envoi de message - fixe en bas */}
        {canSendMessage && (
          <div className="border-t border-gray-200 p-4 bg-white">
            <div className="flex items-start gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Écrire un message..."
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="internalNote"
                checked={isInternalNote}
                onChange={(e) => setIsInternalNote(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="internalNote" className="text-sm text-gray-600">
                Note interne (visible seulement par les agents)
              </label>
            </div>
          </div>
        )}

        {/* Actions - changement de statut (en bas) */}
        {onStatusChange && ticket.status !== "ferme" && ticket.status !== "resolu" && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Changer le statut</h4>
            <div className="flex flex-wrap gap-2">
              {ticket.status !== "en_cours" && (
                <button
                  onClick={() => onStatusChange(ticket.id, "en_cours")}
                  className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  Prendre en charge
                </button>
              )}
              {ticket.status !== "en_attente" && (
                <button
                  onClick={() => onStatusChange(ticket.id, "en_attente")}
                  className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                >
                  Mettre en attente
                </button>
              )}
              {ticket.status !== "escalade" && (
                <button
                  onClick={() => onStatusChange(ticket.id, "escalade")}
                  className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Escalader
                </button>
              )}
              <button
                onClick={() => onStatusChange(ticket.id, "resolu")}
                className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
              >
                Marquer comme résolu
              </button>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
}
